/**
 * Date utilities for age validation and date calculations
 * 
 * @example
 * // For a date input with minimum age 50:
 * <CustomFormItem
 *   type="date"
 *   max={dateUtils.getMaxDateForAge50()}
 *   rules={['isRequired', 'minimumAge50']}
 * />
 * 
 * @example
 * // For a custom minimum age:
 * <CustomFormItem
 *   type="date"
 *   max={dateUtils.getMaxDateForMinimumAge(25)}
 *   rules={['isRequired', validationUtils.createMinimumAgeValidator(25)]}
 * />
 * 
 * @example
 * // Check age programmatically:
 * const age = dateUtils.calculateAge('1990-05-15')
 * const isOldEnough = dateUtils.meetsMinimumAge('1990-05-15', 18)
 */

/**
 * Calculates the maximum allowed date for a minimum age requirement
 * @param minimumAge - The minimum age in years
 * @returns A date string in YYYY-MM-DD format representing the maximum allowed birth date
 */
export const getMaxDateForMinimumAge = (minimumAge: number): string => {
  const today = new Date()
  const maxYear = today.getFullYear() - minimumAge
  const maxMonth = String(today.getMonth() + 1).padStart(2, '0')
  const maxDay = String(today.getDate()).padStart(2, '0')
  
  return `${maxYear}-${maxMonth}-${maxDay}`
}

/**
 * Calculates age from a given date
 * @param dateOfBirth - The date of birth as a string or Date object
 * @returns The calculated age in years
 */
export const calculateAge = (dateOfBirth: string | Date): number => {
  const birthDate = new Date(dateOfBirth)
  const today = new Date()
  const age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  
  // Adjust age if birthday hasn't occurred this year
  return monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate()) ? age - 1 : age
}

/**
 * Validates if a person meets the minimum age requirement
 * @param dateOfBirth - The date of birth as a string or Date object
 * @param minimumAge - The minimum required age
 * @returns true if the person meets the minimum age, false otherwise
 */
export const meetsMinimumAge = (dateOfBirth: string | Date, minimumAge: number): boolean => {
  return calculateAge(dateOfBirth) >= minimumAge
}

// Common age requirements for easy reuse
export const COMMON_AGE_LIMITS = {
  /** Minimum age for most financial products */
  MINIMUM_AGE_50: 50,
  /** Legal adult age */
  MINIMUM_AGE_18: 18,
  /** Senior age threshold */
  MINIMUM_AGE_65: 65,
} as const

// Pre-calculated max dates for common age requirements
export const getMaxDateForAge50 = () => getMaxDateForMinimumAge(COMMON_AGE_LIMITS.MINIMUM_AGE_50)
export const getMaxDateForAge18 = () => getMaxDateForMinimumAge(COMMON_AGE_LIMITS.MINIMUM_AGE_18)
export const getMaxDateForAge65 = () => getMaxDateForMinimumAge(COMMON_AGE_LIMITS.MINIMUM_AGE_65)